import { useState } from "react";
import CapabilityTabs from "@/components/site/CapabilityTabs";
import PageHero from "@/components/site/PageHero";
import { Reveal, ScaleImage } from "@/components/site/Reveal";
import Lightbox from "@/components/site/Lightbox";
import { EQUIPMENT, INFRASTRUCTURE } from "@/data/content";

function SummaryBlock({ title, items, testid }) {
  return (
    <div data-testid={testid}>
      <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-6">{title}</p>
      <div className="border-t border-l border-border">
        {items.map((s) => (
          <div key={s.label} className="flex items-center justify-between border-r border-b border-border px-5 py-4">
            <span className="text-sm md:text-base">{s.label}</span>
            <span className="font-mono-tech text-sm text-[hsl(var(--signal))]">{s.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Infrastructure() {
  const [lbIndex, setLbIndex] = useState(null);
  return (
    <div data-testid="infrastructure-page">
      <PageHero
        index="/ 03.1"
        overline="Capabilities — Infrastructure"
        title={<>Machines and units built<br />for heavy, exact work.</>}
        subtitle="Large-envelope machining, CNC turning and marine-grade welding across three coordinated manufacturing units in Thane and Ambernath."
      />
      <CapabilityTabs />

      {/* Equipment summaries */}
      <section className="py-14 md:py-16">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <SummaryBlock title="[ Machine Shop ]" items={EQUIPMENT.summary} testid="equipment-summary" />
          </Reveal>
          <Reveal delay={0.1}>
            <SummaryBlock title="[ Welding Equipment ]" items={EQUIPMENT.welding} testid="welding-summary" />
          </Reveal>
        </div>
      </section>

      {/* Machine specifications */}
      <section className="pb-16 md:pb-20">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-10">
              [ Machine Specifications ]
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
            {EQUIPMENT.machines.map((m, i) => (
              <Reveal key={m.name} delay={(i % 3) * 0.08}>
                <div
                  data-testid={`spec-card-${i}`}
                  className="group flex flex-col h-full border-r border-b border-border hover:bg-secondary transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => setLbIndex(i)}
                    aria-label={`View ${m.name}`}
                    data-testid={`spec-image-${i}`}
                    className="block w-full text-left cursor-pointer"
                  >
                    <ScaleImage src={m.image} alt={m.name} className="h-52" />
                  </button>
                  <div className="p-7 flex flex-col flex-1">
                    <div className="flex items-baseline justify-between mb-1">
                      <h3 className="font-display font-medium text-2xl tracking-tight">{m.name}</h3>
                      <span className="font-mono-tech text-[10px] text-[hsl(var(--signal))]">0{i + 1}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mb-6">{m.type}</p>
                    <dl className="mt-auto space-y-2.5 font-mono-tech text-[12px]">
                      <div className="flex justify-between gap-4 border-t border-border pt-2.5">
                        <dt className="text-muted-foreground uppercase tracking-wider">Travel X/Y/Z</dt>
                        <dd className="text-right">{m.travel}</dd>
                      </div>
                      <div className="flex justify-between gap-4 border-t border-border pt-2.5">
                        <dt className="text-muted-foreground uppercase tracking-wider">Table</dt>
                        <dd className="text-right">{m.table}</dd>
                      </div>
                      <div className="flex justify-between gap-4 border-t border-border pt-2.5">
                        <dt className="text-muted-foreground uppercase tracking-wider">Capacity</dt>
                        <dd className="text-right font-semibold">{m.capacity}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Facility photos */}
      <section className="pb-14 md:pb-20">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-10">
              [ Inside the Facility ]
            </p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              ...INFRASTRUCTURE.units.map((u) => u.image),
              ...INFRASTRUCTURE.gallery,
            ]
              .filter((src, i, arr) => arr.indexOf(src) === i)
              .slice(0, 3)
              .map((src, i) => (
                <Reveal key={src} delay={(i % 3) * 0.06}>
                  <ScaleImage src={src} alt="Facility" className="h-44 md:h-72" />
                </Reveal>
              ))}
          </div>
        </div>
      </section>

      <Lightbox
        open={lbIndex !== null}
        images={EQUIPMENT.machines.map((m) => m.image)}
        index={lbIndex ?? 0}
        title={lbIndex !== null ? EQUIPMENT.machines[lbIndex].name : ""}
        client={lbIndex !== null ? EQUIPMENT.machines[lbIndex].type : ""}
        onClose={() => setLbIndex(null)}
        onIndexChange={setLbIndex}
      />
    </div>
  );
}
